import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { formatCurrency, formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { decimalsFor } from '@/lib/shared-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableEmpty, TableWrap } from '@/components/ui/table';
import { idempotencyKey } from './lib';
import { CustomerCombobox } from './Wallet';
import {
  WalletAccountKind,
  WalletAccountStatus,
  alreadyReversed,
  apiError,
  isReversible,
  type AdjustWalletBody,
  type CloseWalletBody,
  type ReloadWalletBody,
  type ReverseWalletBody,
  type TransferWalletBody,
  type WalletAccountDetail as Detail,
  type WalletPosting,
  type WalletTransaction,
} from './walletApi';

const LEDGER_PAGE = 20;

/**
 * One wallet — its balance, status, expiry and policy, then the full ledger.
 * The ledger is the primary content, not a footnote: every figure the header
 * shows is a projection of the rows below it, and a customer's first question
 * about a wrong balance ("what happened to it") is answered here or nowhere.
 */
export default function WalletAccountDetail({
  accountId,
  onChanged,
}: {
  accountId: string;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { confirm, prompt } = useConfirm();

  const canReload = can('wallet.reload');
  const canAdjust = can('wallet.adjust');
  const canClose = can('wallet.close');
  const canIssue = can('wallet.issue'); // activate + transfer share this key on the backend
  const canRefund = can('wallet.refund');

  const [account, setAccount] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ledger, setLedger] = useState<WalletTransaction[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [ledgerError, setLedgerError] = useState<string | null>(null);

  const [reloadOpen, setReloadOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [busyAction, setBusyAction] = useState(false);

  const loadAccount = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<Detail>(`/wallet/${accountId}`)
      .then(({ data }) => setAccount(data))
      // A 500 here is `assertLedgerIsReadable` refusing to report a broken read
      // as an empty wallet (ADR-0012 Decision 6) — it must land as an error, not
      // a quiet zero balance, so the fallback message says so plainly.
      .catch((e) => setError(apiError(e, t('wallet.detailLoadFailed'))))
      .finally(() => setLoading(false));
  }, [accountId, t]);

  const loadLedger = useCallback(() => {
    setLedgerLoading(true);
    setLedgerError(null);
    api
      .get<{ data: WalletTransaction[]; meta: { total: number } }>(
        `/wallet/${accountId}/transactions`,
        { params: { page: ledgerPage, limit: LEDGER_PAGE } },
      )
      .then(({ data }) => {
        setLedger(data.data ?? []);
        setLedgerTotal(data.meta?.total ?? 0);
      })
      .catch((e) => setLedgerError(apiError(e, t('wallet.ledgerLoadFailed'))))
      .finally(() => setLedgerLoading(false));
  }, [accountId, ledgerPage, t]);

  useEffect(() => {
    setLedgerPage(1);
  }, [accountId]);

  useEffect(loadAccount, [loadAccount]);
  useEffect(loadLedger, [loadLedger]);

  const refreshAll = () => {
    loadAccount();
    loadLedger();
    onChanged();
  };

  if (loading) return <LoadingState label={t('common.loading')} />;
  if (error) return <ErrorState message={error} onRetry={loadAccount} />;
  if (!account) return <EmptyState title={t('wallet.notFound')} />;

  const isCard = account.kind === WalletAccountKind.GIFT_CARD;
  // Server-computed, both of them — never a client-side sum. `ledgerBalance` is
  // the one ADR-0012 calls authoritative when the two disagree; `balance` is the
  // maintained cache the list screen uses for its one-query-per-page read.
  const displayBalance = formatCurrency(account.ledgerBalance, account.currency, i18n.language);
  const drift = Number(account.balance) !== Number(account.ledgerBalance);

  const expiryTone: 'danger' | 'warning' | 'neutral' = !account.expiresAt
    ? 'neutral'
    : new Date(account.expiresAt).getTime() <= Date.now()
      ? 'danger'
      : new Date(account.expiresAt).getTime() - Date.now() < 7 * 86_400_000
        ? 'warning'
        : 'neutral';

  const activate = async () => {
    if (!(await confirm({ title: t('wallet.activateConfirm') }))) return;
    setBusyAction(true);
    try {
      await api.post(`/wallet/${account.id}/activate`, {});
      toast.success(t('wallet.activated'));
      refreshAll();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusyAction(false);
    }
  };

  const suspend = async () => {
    if (!(await confirm({ title: t('wallet.suspendConfirm'), tone: 'danger' }))) return;
    setBusyAction(true);
    try {
      await api.post(`/wallet/${account.id}/suspend`, {});
      toast.success(t('wallet.suspended'));
      refreshAll();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusyAction(false);
    }
  };

  const resume = async () => {
    if (!(await confirm({ title: t('wallet.resumeConfirm') }))) return;
    setBusyAction(true);
    try {
      await api.post(`/wallet/${account.id}/resume`, {});
      toast.success(t('wallet.resumed'));
      refreshAll();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusyAction(false);
    }
  };

  const close = async () => {
    const reason = await prompt({
      title: t('wallet.closeConfirm'),
      body: account.balance !== '0' ? t('wallet.closeWriteOffHint') : undefined,
      label: t('wallet.reason'),
      tone: 'danger',
      confirmLabel: t('wallet.closeConfirmLabel'),
    });
    if (reason === null) return;
    setBusyAction(true);
    try {
      const body: CloseWalletBody = {
        reason: reason || undefined,
        idempotencyKey: idempotencyKey(`wallet-close:${account.id}`),
      };
      await api.post(`/wallet/${account.id}/close`, body);
      toast.success(t('wallet.closed'));
      refreshAll();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusyAction(false);
    }
  };

  const reverse = async (line: WalletTransaction) => {
    const reason = await prompt({
      title: t('wallet.reverseConfirm'),
      body: t('wallet.reverseHint', {
        amount: formatCurrency(
          (Number(line.amount) < 0 ? -Number(line.amount) : Number(line.amount)).toFixed(
            decimalsFor(line.currency),
          ),
          line.currency,
          i18n.language,
        ),
      }),
      label: t('wallet.reason'),
      tone: 'danger',
      confirmLabel: t('wallet.reverseConfirmLabel'),
    });
    if (reason === null) return;
    try {
      const body: ReverseWalletBody = {
        reason: reason || undefined,
        idempotencyKey: idempotencyKey(`wallet-reverse:${line.id}`),
      };
      await api.post<WalletPosting>(`/wallet/transactions/${line.id}/reverse`, body);
      toast.success(t('wallet.reversed'));
      refreshAll();
    } catch (e) {
      toast.error(apiError(e, t('wallet.reverseFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold">
                  {isCard
                    ? account.codeLast4
                      ? t('wallet.maskedCode', { last4: account.codeLast4 })
                      : t('wallet.giftCard')
                    : t('wallet.storeCredit')}
                </h2>
                <Badge tone={toneForStatus(account.status)}>
                  {t(`wallet.status.${account.status}`)}
                </Badge>
              </div>
              <p className="text-xs text-ink-muted">
                {t('wallet.issuedAt', { date: formatDateTime(account.createdAt, i18n.language) })}
                {account.note ? ` · ${account.note}` : ''}
              </p>
              {account.customerId && (
                <CustomerName customerId={account.customerId} fallback={account.customer?.name} />
              )}
            </div>
            <div className="text-end">
              <p className="text-2xl font-semibold tabular-nums">{displayBalance}</p>
              <p className="text-xs text-ink-muted">{t('wallet.balance')}</p>
            </div>
          </div>

          {drift && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t('wallet.drift', {
                stored: formatCurrency(account.balance, account.currency, i18n.language),
                ledger: displayBalance,
              })}
            </p>
          )}

          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                {t('wallet.kindLabel')}
              </dt>
              <dd>{t(`wallet.kind.${account.kind}`)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                {t('wallet.expiresAt')}
              </dt>
              <dd
                className={
                  expiryTone === 'danger'
                    ? 'text-red-600 dark:text-red-400'
                    : expiryTone === 'warning'
                      ? 'text-amber-600'
                      : undefined
                }
              >
                {account.expiresAt ? formatDateTime(account.expiresAt, i18n.language) : t('wallet.noExpiry')}
              </dd>
            </div>
            {isCard && (
              <>
                <div>
                  <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                    {t('wallet.reloadable')}
                  </dt>
                  <dd>{account.reloadable ? t('common.yes') : t('common.no')}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                    {t('wallet.transferable')}
                  </dt>
                  <dd>{account.transferable ? t('common.yes') : t('common.no')}</dd>
                </div>
              </>
            )}
          </dl>

          <div className="flex flex-wrap gap-2 border-t pt-4">
            {canIssue && account.status === WalletAccountStatus.INACTIVE && (
              <Button size="sm" onClick={() => void activate()} disabled={busyAction}>
                {t('wallet.activate')}
              </Button>
            )}
            {canReload &&
              account.status === WalletAccountStatus.ACTIVE &&
              account.reloadable && (
                <Button size="sm" variant="outline" onClick={() => setReloadOpen(true)}>
                  {t('wallet.reload')}
                </Button>
              )}
            {canAdjust && account.status !== WalletAccountStatus.CLOSED && (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40"
                onClick={() => setAdjustOpen(true)}
              >
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {t('wallet.adjust')}
              </Button>
            )}
            {canIssue &&
              isCard &&
              account.transferable &&
              account.status !== WalletAccountStatus.CLOSED && (
                <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)}>
                  {t('wallet.transfer')}
                </Button>
              )}
            {canClose &&
              (account.status === WalletAccountStatus.ACTIVE ||
                account.status === WalletAccountStatus.INACTIVE) && (
                <Button size="sm" variant="ghost" onClick={() => void suspend()} disabled={busyAction}>
                  {t('wallet.suspend')}
                </Button>
              )}
            {canClose && account.status === WalletAccountStatus.SUSPENDED && (
              <Button size="sm" variant="ghost" onClick={() => void resume()} disabled={busyAction}>
                {t('wallet.resume')}
              </Button>
            )}
            {canClose && account.status !== WalletAccountStatus.CLOSED && (
              <Button size="sm" variant="danger" onClick={() => void close()} disabled={busyAction}>
                {t('wallet.close')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('wallet.ledger')}</CardTitle>
          <p className="text-xs text-ink-muted">{t('wallet.ledgerSubtitle')}</p>
        </CardHeader>
        <CardContent className="p-0">
          {ledgerLoading ? (
            <LoadingState label={t('common.loading')} />
          ) : ledgerError ? (
            // Same distinction as above: under RLS a broken read answers zero
            // rows, not an error, so a card with real history could otherwise
            // render as "no transactions". This branch is what stops that.
            <ErrorState message={ledgerError} onRetry={loadLedger} />
          ) : ledger.length === 0 ? (
            <EmptyState title={t('wallet.ledgerEmpty')} />
          ) : (
            <TableWrap className="rounded-none border-x-0 border-t-0">
              <Table>
                <THead>
                  <tr>
                    <TH>{t('wallet.date')}</TH>
                    <TH>{t('wallet.type')}</TH>
                    <TH>{t('wallet.reason')}</TH>
                    <TH>{t('wallet.reference')}</TH>
                    <TH className="text-end">{t('wallet.amount')}</TH>
                    <TH className="text-end">{t('wallet.balanceAfter')}</TH>
                    {canRefund && <TH className="text-end">{t('common.actions')}</TH>}
                  </tr>
                </THead>
                <tbody>
                  {ledger.length === 0 ? (
                    <TableEmpty colSpan={canRefund ? 7 : 6}>{t('wallet.ledgerEmpty')}</TableEmpty>
                  ) : (
                    ledger.map((line) => {
                      const positive = Number(line.amount) >= 0;
                      // Best-effort against the loaded page only — a reversal can
                      // sit on an earlier page than the line it reverses. The
                      // server is the authority either way: a stale "reverse"
                      // button gets a 409 with a clear message, never a double
                      // refund (ADR-0012 Decision 1 — `reversesId` is unique).
                      const reversible =
                        canRefund && isReversible(line.type) && !alreadyReversed(ledger, line.id);
                      return (
                        <TR key={line.id}>
                          <TD className="text-xs text-ink-muted">
                            {formatDateTime(line.createdAt, i18n.language)}
                          </TD>
                          <TD>{t(`wallet.txType.${line.type}`)}</TD>
                          <TD className="max-w-xs truncate" title={line.reason}>
                            {line.reason}
                          </TD>
                          <TD className="text-xs text-ink-muted">
                            {line.refType
                              ? t('wallet.refLine', { type: line.refType, id: line.refId ?? '—' })
                              : '—'}
                          </TD>
                          <TD
                            className={`text-end tabular-nums ${
                              positive
                                ? 'text-green-700 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}
                          >
                            {positive ? '+' : ''}
                            {formatCurrency(line.amount, line.currency, i18n.language)}
                          </TD>
                          <TD className="text-end tabular-nums">
                            {formatCurrency(line.balanceAfter, line.currency, i18n.language)}
                          </TD>
                          {canRefund && (
                            <TD className="text-end">
                              {reversible && (
                                <Button size="sm" variant="ghost" onClick={() => void reverse(line)}>
                                  {t('wallet.reverse')}
                                </Button>
                              )}
                            </TD>
                          )}
                        </TR>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </TableWrap>
          )}

          {ledgerTotal > LEDGER_PAGE && (
            <div className="flex items-center justify-between p-3 text-xs text-ink-muted">
              <Button
                size="sm"
                variant="ghost"
                disabled={ledgerPage <= 1}
                onClick={() => setLedgerPage((p) => Math.max(1, p - 1))}
              >
                {t('common.previous')}
              </Button>
              <span>
                {t('wallet.pageOf', {
                  page: ledgerPage,
                  pages: Math.ceil(ledgerTotal / LEDGER_PAGE),
                })}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={ledgerPage >= Math.ceil(ledgerTotal / LEDGER_PAGE)}
                onClick={() =>
                  setLedgerPage((p) => Math.min(Math.ceil(ledgerTotal / LEDGER_PAGE), p + 1))
                }
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {reloadOpen && (
        <ReloadDialog account={account} onClose={() => setReloadOpen(false)} onDone={refreshAll} />
      )}
      {adjustOpen && (
        <AdjustDialog account={account} onClose={() => setAdjustOpen(false)} onDone={refreshAll} />
      )}
      {transferOpen && (
        <TransferDialog account={account} onClose={() => setTransferOpen(false)} onDone={refreshAll} />
      )}
    </div>
  );
}

/**
 * `GET /wallet/:id` does not include the customer relation (only `findAll`
 * does), so the name is a best-effort second read. A failure here is cosmetic
 * — the id still shows — and must never be confused with a broken ledger read.
 */
function CustomerName({ customerId, fallback }: { customerId: string; fallback?: string | null }) {
  const { t } = useTranslation();
  const [name, setName] = useState<string | null>(fallback ?? null);

  useEffect(() => {
    if (fallback) return;
    api
      .get<{ name: string | null }>(`/customers/${customerId}`)
      .then(({ data }) => setName(data.name))
      .catch(() => setName(null));
  }, [customerId, fallback]);

  return (
    <p className="text-xs text-ink-muted">
      {t('wallet.registeredTo', { name: name ?? customerId })}
    </p>
  );
}

function ReloadDialog({
  account,
  onClose,
  onDone,
}: {
  account: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const decimals = decimalsFor(account.currency);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => idempotencyKey(`wallet-reload:${account.id}`));

  const valid = Number(amount) > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body: ReloadWalletBody = {
        amount: Number(amount).toFixed(decimals),
        reason: reason || undefined,
        idempotencyKey: key,
      };
      await api.post<WalletPosting>(`/wallet/${account.id}/reload`, body);
      toast.success(t('wallet.reloaded'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wallet.reload')}
      description={t('wallet.reloadHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !valid}>
            {t('wallet.reload')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('wallet.amount')}
          required
          hint={t('currency.amountIn', { code: account.currency })}
        >
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step={decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label={t('wallet.reason')} hint={t('wallet.reloadReasonHint')}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * §3 — the manual adjustment. Permissioned on `wallet.adjust`, which no floor
 * role holds; the reason is mandatory; the amount is SIGNED and the dialog
 * never lets that fact hide — it is stated beside the field, not just in a
 * validation message. This is the loudest path in the module and the UI is
 * built to say so, not to soften it into a quiet balance edit.
 */
function AdjustDialog({
  account,
  onClose,
  onDone,
}: {
  account: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const decimals = decimalsFor(account.currency);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => idempotencyKey(`wallet-adjust:${account.id}`));

  const numeric = Number(amount);
  const valid = amount.trim() !== '' && numeric !== 0 && reason.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    try {
      const body: AdjustWalletBody = {
        amount: numeric.toFixed(decimals),
        reason: reason.trim(),
        idempotencyKey: key,
      };
      await api.post<WalletPosting>(`/wallet/${account.id}/adjust`, body);
      toast.success(t('wallet.adjusted'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wallet.adjust')}
      description={t('wallet.adjustHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={() => void submit()} disabled={busy || !valid}>
            {t('wallet.adjust')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {t('wallet.adjustWarning')}
        </p>
        <Field
          label={t('wallet.signedAmount')}
          required
          hint={t('wallet.signedAmountHint', { code: account.currency })}
        >
          <Input
            type="number"
            inputMode="decimal"
            step={decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="-15.00"
          />
        </Field>
        <Field
          label={t('wallet.reason')}
          required
          hint={t('wallet.adjustReasonHint')}
        >
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function TransferDialog({
  account,
  onClose,
  onDone,
}: {
  account: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [customer, setCustomer] = useState<{ id: string; label: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!customer) return;
    setBusy(true);
    try {
      const body: TransferWalletBody = { customerId: customer.id };
      await api.post(`/wallet/${account.id}/transfer`, body);
      toast.success(t('wallet.transferred'));
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('wallet.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('wallet.transfer')}
      description={t('wallet.transferHint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={busy || !customer}>
            {t('wallet.transfer')}
          </Button>
        </>
      }
    >
      <CustomerCombobox value={customer} onChange={setCustomer} required />
    </Dialog>
  );
}
